import {
  Component,
  inject,
  signal,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { StatusModalService } from '../../core/services/status-modal.service';
import { MatDialog } from '@angular/material/dialog';
import { AsyncPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { DNSZones, DNSZoneStatus } from '../../shared/models/dns.models';
import { DNSService } from '../../core/services/dns.service';
import { TableComponent } from '../../shared/components/table/table.component';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { ServersService } from '../../core/services/servers.service';
import {
  combineLatest,
  map,
  startWith,
  Subject,
  switchMap,
  of,
  catchError,
  filter,
  takeUntil,
  shareReplay,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import { Status } from '../../shared/models/status-label.model';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { WebsocketService } from '../../core/services/websocket.service';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { AddDnsZoneDialogComponent } from './dialogs/add-dns-zone/add-dns-zone-dialog.component';
import { SyncDnsZonesDialogComponent } from './dialogs/sync-dns-zones-dialog/sync-dns-zones-dialog.component';
import { SyncDnsServerDialogComponent } from './dialogs/sync-dns-server-dialog/sync-dns-server-dialog.component';
import { AddDnsZoneDialogResult } from './models/add-dns-zone.model';
import { SyncDnsServerDialogResult } from './models/sync-dns-server.model';
import { Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';

@Component({
  selector: 'app-all-dns-zones',
  imports: [
    AsyncPipe,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    TableComponent,
    StatusLabelComponent,
    SearchFieldComponent,
    FontAwesomeModule,
    MatTooltipModule,
  ],
  templateUrl: './all-dns-zones.component.html',
  styleUrl: './all-dns-zones.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllDnsZonesComponent implements OnInit, OnDestroy {
  private dnsService = inject(DNSService);

  private serversService = inject(ServersService);

  private statusModalService = inject(StatusModalService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private websocketService = inject(WebsocketService);

  private readonly router = inject(Router);

  private readonly refresh$ = new Subject<void>();

  private readonly destroyed$ = new Subject();

  readonly searchText = signal('');

  private readonly searchText$ = toObservable(this.searchText).pipe(
    debounceTime(500),
    distinctUntilChanged(),
  );

  dnsZoneStatus = DNSZoneStatus;

  readonly pagination = signal({ page: 1, pageSize: 10 });

  readonly pagination$ = toObservable(this.pagination);

  readonly zonesResponse$ = combineLatest([
    this.searchText$,
    this.pagination$,
    this.refresh$.pipe(startWith(undefined)),
  ]).pipe(
    switchMap(([searchText, { page, pageSize }]) => {
      if (searchText.length > 0 && searchText.length < 3) {
        return of({
          zones: [] as DNSZones[],
          totalCount: 0,
        });
      }

      return this.dnsService
        .getAllZones({
          page,
          itemsPerPage: pageSize,
          search: searchText || undefined,
        })
        .pipe(
          switchMap(([zones, totalCount]) => {
            if (!zones.length) {
              return of({ zones, totalCount });
            }

            const serverIds = [...new Set(zones.map((zone) => zone.installation.server))];

            return this.serversService.getServersByIds(serverIds).pipe(
              map((servers) => ({
                zones: zones.map((zone) => ({
                  ...zone,
                  serverHostname:
                    servers.find((server) => server._id === zone.installation.server)?.hostname ||
                    '-',
                })),
                totalCount,
              })),
            );
          }),
          catchError((error) => {
            this.statusModalService.open({
              state: 'error',
              message: error.error?.error || 'Failed to load DNS zones.',
            });

            return of({
              zones: [] as DNSZones[],
              totalCount: 0,
            });
          }),
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly zones$ = this.zonesResponse$.pipe(map(({ zones }) => zones));

  readonly totalCount$ = this.zonesResponse$.pipe(map(({ totalCount }) => totalCount));

  readonly zonesColumns = [
    {
      columnDef: 'domain',
      header: 'Domain',
      cell: (element: DNSZones) => element.installation.domain,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: DNSZones) => element.status || '-',
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: DNSZones) => element.serverHostname || '-',
    },
    {
      columnDef: 'serverIp',
      header: 'Server IP',
      cell: (element: DNSZones) => element.installation.dnsIP,
    },
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: DNSZones) => `${element.id}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ message: string; type: string }>('dialog')
      .pipe(
        takeUntil(this.destroyed$),
        filter(
          ({ message }) =>
            (message.startsWith("DNS Zone '") && message.endsWith("' has been deleted.")) ||
            message.startsWith('Failed to Delete DNS Zone with id:'),
        ),
      )
      .subscribe(({ message, type }) => {
        if (type === 'error') {
          this.statusModalService.open({
            state: 'error',
            message,
          });
        }

        this.refresh$.next();
      });
  }

  handlePageEvent(event: PageEvent): void {
    this.pagination.set({
      page: event.pageIndex + 1,
      pageSize: event.pageSize,
    });
  }

  toStatus(key: DNSZoneStatus): Status {
    switch (key) {
      case DNSZoneStatus.ACTIVE:
        return Status.ACTIVE;
      case DNSZoneStatus.DISABLED:
        return Status.DISABLED;
      case DNSZoneStatus.PENDING:
        return Status.PENDING;
      case DNSZoneStatus.SYNCERROR:
        return Status.SYNCERROR;
      case DNSZoneStatus.DUPLICATE:
        return Status.DUPLICATE;
      default:
        return Status.ERROR;
    }
  }

  onSearch(text: string): void {
    const searchText = text.trim();

    if (this.searchText() !== searchText) {
      this.pagination.update((pagination) => ({
        ...pagination,
        page: 1,
      }));

      this.searchText.set(searchText);
    }
  }

  activateDNSZone(zone: DNSZones): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Activate DNS Zone',
          paragraphs: [
            `This action will activate ${zone.installation.domain} DNS Zone. The previous active DNS Zone will be deleted.`,
            `Are you sure you want to activate new DNS Zone for ${zone.installation.domain}?`,
          ],
          primaryButtonText: 'Activate DNS Zone',
        },
      })
      .afterClosed()
      .pipe(
        filter((confirmed) => confirmed),
        switchMap(() => this.dnsService.activateDNSZone(zone.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `DNS Zone ${zone.installation.domain} activation started.`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to activate DNS Zone.',
          });
        },
      });
  }

  deleteDNSZone(zone: DNSZones): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete DNS Zone',
        paragraphs: [
          `Deleting the DNS Zone of ${zone.installation.domain} may cause downtime of the website.`,
          `Any custom DNS Records will be lost.`,
          `Are you sure you want to delete ${zone.installation.domain}?`,
        ],
        primaryButtonText: 'Delete DNS Zone',
        type: 'delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.dnsService.deleteDNSZone(zone.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Deletion of DNS Zone ${zone.installation.domain} started.`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to delete DNS Zone.',
          });
        },
      });
  }

  editDNSZone(zone: DNSZones): void {
    this.router.navigate(['/overview', zone.websiteId], {
      queryParams: { tab: 'dns' },
    });
  }

  addDNSZone(): void {
    const dialogRef = this.dialog.open<AddDnsZoneDialogComponent, void, AddDnsZoneDialogResult>(
      AddDnsZoneDialogComponent,
    );

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is AddDnsZoneDialogResult => !!result),
        switchMap(({ domain, ...payload }) =>
          this.dnsService.createDNSZone(payload).pipe(map(() => domain)),
        ),
      )
      .subscribe({
        next: (domain) => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Adding DNS Zone for ${domain} started.`,
            },
          });

          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to create DNS Zone.',
          });
        },
      });
  }

  syncDNSZones(): void {
    const dialogRef = this.dialog.open(SyncDnsZonesDialogComponent);
    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((result) => this.dnsService.syncDNS(result)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Synchronization of all DNS Zones started.`,
            },
          });
          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to sync DNS Zones.',
          });
        },
      });
  }

  syncDNSServer(): void {
    const dialogRef = this.dialog.open<
      SyncDnsServerDialogComponent,
      void,
      SyncDnsServerDialogResult
    >(SyncDnsServerDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(
        filter((result): result is SyncDnsServerDialogResult => !!result),
        switchMap(({ server, serverHostname, step }) =>
          this.dnsService.syncDNS({ server, step }).pipe(map(() => serverHostname)),
        ),
      )
      .subscribe({
        next: (serverHostname) => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Synchronization of all DNS Zones on ${serverHostname} started.`,
            },
          });

          this.refresh$.next();
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to sync DNS Server.',
          });
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next('');
    this.destroyed$.complete();
  }
}
